// Zerodha MCP Server integration utilities
import { supabase } from '@/integrations/supabase/client';

// MCP Server Configuration
const MCP_SERVER_URL = 'https://mcp.kite.trade/mcp';

export interface MCPPortfolioQuery {
  type: 'portfolio' | 'holdings' | 'positions' | 'market' | 'analysis';
  query: string;
  userContext?: {
    userId: string;
    hasKiteConnection: boolean;
  };
}

export interface MCPResponse {
  success: boolean;
  data?: any;
  error?: string;
  needsAuth?: boolean;
}

// Detect if a user query is portfolio-related
export const isPortfolioQuery = (message: string): boolean => {
  const portfolioKeywords = [
    'portfolio', 'holdings', 'positions', 'stocks', 'shares',
    'profit', 'loss', 'pnl', 'investment', 'trades', 'zerodha',
    'my stocks', 'my investments', 'my portfolio', 'my holdings',
    'current position', 'stock performance', 'sector analysis',
    'diversification', 'allocation', 'exposure'
  ];
  
  const lowerMessage = message.toLowerCase();
  return portfolioKeywords.some(keyword => lowerMessage.includes(keyword));
};

// Classify the type of portfolio query
export const classifyPortfolioQuery = (message: string): MCPPortfolioQuery['type'] => {
  const lowerMessage = message.toLowerCase();
  
  if (lowerMessage.includes('holding') || lowerMessage.includes('own')) {
    return 'holdings';
  }
  if (lowerMessage.includes('position') || lowerMessage.includes('trade')) {
    return 'positions';
  }
  if (lowerMessage.includes('market') || lowerMessage.includes('price')) {
    return 'market';
  }
  if (lowerMessage.includes('analysis') || lowerMessage.includes('performance') || 
      lowerMessage.includes('sector') || lowerMessage.includes('diversif')) {
    return 'analysis';
  }
  
  return 'portfolio';
};

// Enhanced AI chat that includes portfolio context
export const sendMCPEnhancedQuery = async (
  message: string, 
  userId: string
): Promise<{ response: string; usedMCP: boolean }> => {
  try {
    // Check if user has Kite connection (check for access token)
    const kiteToken = localStorage.getItem('kite_access_token');
    const hasKiteConnection = !!kiteToken;
    
    // Debug connection detection
    console.log('DEBUG: Connection check:', {
      hasKiteConnection,
      tokenExists: !!kiteToken,
      tokenLength: kiteToken?.length || 0,
      userId
    });
    
    // Determine if this is a portfolio-related query
    const isPortfolioRelated = isPortfolioQuery(message);
    
    console.log('DEBUG: Query analysis:', {
      isPortfolioRelated,
      message: message.substring(0, 50) + '...'
    });
    
    if (isPortfolioRelated && hasKiteConnection) {
      // Get user's portfolio data for context
      const portfolioContext = await getPortfolioContext(userId);
      
      // Enhanced message with portfolio context
      const enhancedMessage = `
User Query: ${message}

Portfolio Context (from user's Zerodha account):
${portfolioContext}

Please provide insights based on this actual portfolio data. Be specific about the user's holdings and provide actionable advice.
      `.trim();
      
      // Send to AI with portfolio context
      console.log('DEBUG: Calling edge function with:', { 
        functionName: 'chat-with-ai',
        portfolioEnhanced: true,
        userId,
        messageLength: enhancedMessage.length
      });
      
      const { data, error } = await supabase.functions.invoke('chat-with-ai', {
        body: { 
          message: enhancedMessage,
          portfolioEnhanced: true,
          userId: userId
        }
      });
      
      console.log('DEBUG: Edge function response:', { data, error });
      
      if (error) {
        console.error('DEBUG: Edge function error details:', error);
        throw error;
      }
      
      return {
        response: data.generatedText || "I couldn't analyze your portfolio at this time.",
        usedMCP: true
      };
    } else if (isPortfolioRelated && !hasKiteConnection) {
      // Portfolio query but no connection
      const connectionMessage = `
I'd love to help you with your portfolio analysis, but I don't have access to your Zerodha account data yet. 

To get personalized insights about your actual holdings and positions, please:

1. Connect your Zerodha account in the Portfolio section
2. Authorize access to your holdings
3. Then ask me again for detailed analysis

For now, I can provide general investment advice. What would you like to know about investing?
      `.trim();
      
      return {
        response: connectionMessage,
        usedMCP: false
      };
    } else {
      // Regular AI query (not portfolio-related)
      const { data, error } = await supabase.functions.invoke('chat-with-ai', {
        body: { message }
      });
      
      if (error) throw error;
      
      return {
        response: data.generatedText || "I'm sorry, I couldn't process your request at this time.",
        usedMCP: false
      };
    }
  } catch (error) {
    return {
      response: "I encountered an error while processing your request. Please try again.",
      usedMCP: false
    };
  }
};

// Get portfolio context for AI analysis
const getPortfolioContext = async (userId: string): Promise<string> => {
  try {
    // Fetch holdings data
    const { data: holdings, error: holdingsError } = await supabase
      .from('kite_holdings')
      .select('*')
      .eq('user_id', userId)
      .order('tradingsymbol');
    
    // Fetch positions data
    const { data: positions, error: positionsError } = await supabase
      .from('kite_positions')
      .select('*')
      .eq('user_id', userId)
      .order('tradingsymbol');
    
    if (holdingsError) {
      return "Portfolio data unavailable due to database error";
    }
    
    if (positionsError) {
      // Continue if positions error, holdings data is primary
    }
    
    // Check if we have any holdings data
    if (!holdings || holdings.length === 0) {
      return "No portfolio data found - please sync your Zerodha account first";
    }
    
    // Format holdings summary (using user_holdings table structure)
    const holdingsSummary = holdings?.map(h => ({
      symbol: h.tradingsymbol,
      quantity: h.quantity,
      avgPrice: h.average_price,
      currentPrice: h.last_price,
      currentValue: h.current_value,
      pnl: h.pnl,
      pnlPercent: h.pnl_percentage,
      sector: h.sector
    })) || [];
    
    // Calculate totals
    const totalValue = holdings?.reduce((sum, h) => sum + (h.current_value || 0), 0) || 0;
    const totalPnL = holdings?.reduce((sum, h) => sum + (h.pnl || 0), 0) || 0;
    
    // Format context
    return `
PORTFOLIO SUMMARY:
- Total Value: ₹${totalValue.toLocaleString('en-IN')}
- Total P&L: ₹${totalPnL.toLocaleString('en-IN')} (${totalPnL >= 0 ? '+' : ''}${totalValue > 0 ? (totalPnL/totalValue*100).toFixed(2) : '0.00'}%)
- Holdings Count: ${holdings?.length || 0}

TOP HOLDINGS:
${holdingsSummary.slice(0, 10).map(h => 
  `${h.symbol} (${h.sector || 'Unknown'}): ${h.quantity} shares, Value: ₹${(h.currentValue || 0).toLocaleString('en-IN')}, P&L: ${(h.pnlPercent || 0).toFixed(2)}%`
).join('\n')}

ADDITIONAL PORTFOLIO ITEMS: ${positions?.length || 0} items from manual portfolio
    `.trim();
  } catch (error) {
    return "Portfolio data unavailable";
  }
};

// MCP Tools simulation (for future direct MCP integration)
export const mcpTools = {
  getHoldings: async (accessToken: string) => {
    // This would eventually call the MCP server directly
    // For now, we'll use our existing data
    console.log('MCP: Getting holdings data');
    return { success: true, data: [] };
  },
  
  getPositions: async (accessToken: string) => {
    console.log('MCP: Getting positions data');
    return { success: true, data: [] };
  },
  
  getMarketData: async (symbols: string[]) => {
    console.log('MCP: Getting market data for', symbols);
    return { success: true, data: [] };
  },
  
  analyzePortfolio: async (portfolioData: any) => {
    console.log('MCP: Analyzing portfolio');
    return { success: true, analysis: {} };
  }
};

// Portfolio-specific prompt templates
export const portfolioPrompts = {
  diversification: (holdings: any[]) => `
Analyze my portfolio diversification based on these holdings: ${JSON.stringify(holdings)}
Provide recommendations for better sector allocation.
  `,
  
  performance: (holdings: any[]) => `
Analyze the performance of my current holdings: ${JSON.stringify(holdings)}
Which stocks are performing well and which need attention?
  `,
  
  riskAssessment: (portfolio: any) => `
Assess the risk level of my portfolio: ${JSON.stringify(portfolio)}
Suggest ways to optimize risk-reward ratio.
  `
};